import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { FieldErrorMessage, NoddiButton, NoddiInput, noddiToast } from 'noddi-ui-web';
import { formatRegistrationNumber, hasErrorCode } from 'noddi-util';
import { useState } from 'react';
import { isValidNorwegianOrganizationNumber } from '../../../utils/organizationNumber';
import { ApiErrorMessageWithTrans } from '../../Error/ApiErrorMessageWithTrans';

type Props = {
  organizationId: number;
  organizationName: string;
  /** Invalidate the source query so the missing-number gate recomputes. */
  onSaved: () => void;
};

/** Inline recovery for an organization missing its registration number on the invoice checkout. */
export const AddOrganizationNumberForm = ({ organizationId, organizationName, onSaved }: Props) => {
  const [value, setValue] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const digits = value.replace(/\s/g, '');
  const isOrganizationNumberValid = isValidNorwegianOrganizationNumber(digits);

  const {
    mutateAsync: patchRegistrationNumber,
    isPending,
    error: patchRegistrationNumberError
  } = noddiAsync.usePatch({
    type: URLKeys.patchOrganizationRegistrationNumber,
    queryConfig: {
      onSuccess: () => {
        noddiToast.success(t`Organization number saved`);
        onSaved();
      },
      onError: (error) => {
        // Someone else filled it in meanwhile: refresh so the gate sees the number instead of asking again.
        if (hasErrorCode(error, 'organization_registration_number_already_set_error')) {
          onSaved();
        }
      }
    }
  });

  const handleSave = async () => {
    setValidationError(null);
    if (!isValidNorwegianOrganizationNumber(digits)) {
      setValidationError(t`Enter a valid 9-digit organization number`);
      return;
    }

    try {
      await patchRegistrationNumber({ id: organizationId, registrationNumber: digits });
    } catch {
      // Mutation error toast is handled by noddiAsync; keep the input for retry.
    }
  };

  return (
    <div className='flex flex-col gap-2 rounded-lg border border-border p-4'>
      <div className='flex flex-col gap-1'>
        <h3 className='font-semibold text-primary'>
          <Trans>Add organization number</Trans>
        </h3>
        <span className='text-sm text-muted-foreground'>
          <Trans>{organizationName} is missing a registration number. Add it to book on invoice.</Trans>
        </span>
      </div>

      <NoddiInput
        name='organization-number'
        label={t`Organization number`}
        placeholder={t`9 digits`}
        value={value}
        onChange={(e) => {
          const nextDigits = e.target.value.replace(/\D/g, '').slice(0, 9);
          setValue(formatRegistrationNumber(nextDigits));
          setValidationError(null);
        }}
      />
      {(validationError || (digits.length > 0 && !isOrganizationNumberValid)) && (
        <FieldErrorMessage errorMessage={validationError ?? t`Enter a valid 9-digit organization number`} />
      )}
      {patchRegistrationNumberError && <ApiErrorMessageWithTrans error={patchRegistrationNumberError} />}

      <NoddiButton
        variant='primary'
        loading={isPending}
        disabled={isPending || !isOrganizationNumberValid}
        onClick={() => void handleSave()}
      >
        <Trans>Save organization number</Trans>
      </NoddiButton>
    </div>
  );
};
